import pylonConfig from 'Config/pylon_config.json'
import { auth } from '@/auth'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pylon?: { chat_settings: Record<string, any> }
  }
}

const appId = pylonConfig.appId
const isEnabled = appId !== '' && !appId.startsWith('PYLON')

const secret = pylonConfig.identityVerificationSecret
const isVerificationEnabled = secret !== '' && !secret.startsWith('PYLON')

async function computeEmailHash(email: string): Promise<string> {
  // Decode hex-encoded secret to raw bytes (per Pylon docs)
  const secretBytes = new Uint8Array(secret.match(/.{1,2}/g)!.map(byte => parseInt(byte, 16)))
  const key = await crypto.subtle.importKey(
    'raw',
    secretBytes,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const signature = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(email))
  return Array.from(new Uint8Array(signature))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

function loadScript() {
  if (document.getElementById('pylon-widget-script')) return
  const s = document.createElement('script')
  s.id = 'pylon-widget-script'
  s.async = true
  s.src = `https://widget.usepylon.com/widget/${appId}`
  document.body.appendChild(s)
}

async function initPylon() {
  if (!isEnabled) return

  const user = auth.user.value
  if (!user?.email) return

  const fullName = [user.given_name, user.family_name].filter(Boolean).join(' ') || user.name || ''

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chatSettings: Record<string, any> = {
    app_id: appId,
    email: user.email,
    name: fullName
  }

  if (isVerificationEnabled) {
    chatSettings.email_hash = await computeEmailHash(user.email)
  }

  window.pylon = { chat_settings: chatSettings }

  loadScript()
}

export const usePylon = () => ({ initPylon, isEnabled })
